import { useEffect, useMemo, useState, type RefObject } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, DialogClose, FeedbackState, FormDrawer } from "@h2j-data-team/ui";

import {
  createQlikConnectionInputSchema,
  type CreateQlikConnectionInput,
  type QlikAuthMethod,
  type UpdateQlikConnectionInput,
} from "@repo/shared";
import { toast } from "@/components/shared";
import {
  connectionQueryOptions,
  useCreateConnection,
  useUpdateConnection,
} from "@/features/qlik-connections/api";
import { getErrorMessage } from "@/lib/api-client";
import { discardMessages } from "@/lib/ui-labels";
import { makeEditConnectionFormSchema, type EditConnectionFormData } from "../schemas";
import { ConnectionFormFields } from "./connection-form";
import { ConnectionDrawerHelp } from "./connection-drawer-help";

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

const CREATE_FORM_ID = "create-connection-form";

export function CreateConnectionDrawer({
  open,
  onOpenChange,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The button that opened the drawer, where the focus returns on close. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("connections");
  const { t: tc } = useTranslation("common");
  const createMutation = useCreateConnection();
  const [authMethod, setAuthMethod] = useState<QlikAuthMethod>("api_key");

  const form = useForm<CreateQlikConnectionInput>({
    resolver: zodResolver(createQlikConnectionInputSchema),
    defaultValues: { authMethod: "api_key", name: "", baseUrl: "", apiKey: "" },
  });

  // Reset to a clean state each time the drawer opens.
  useEffect(() => {
    if (open) {
      setAuthMethod("api_key");
      form.reset({ authMethod: "api_key", name: "", baseUrl: "", apiKey: "" });
      createMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleMethodChange = (next: QlikAuthMethod) => {
    setAuthMethod(next);
    const common = {
      name: form.getValues("name") ?? "",
      baseUrl: form.getValues("baseUrl") ?? "",
    };
    form.reset(
      next === "oauth_client_credentials"
        ? { authMethod: "oauth_client_credentials", ...common, clientId: "", clientSecret: "" }
        : { authMethod: "api_key", ...common, apiKey: "" },
    );
  };

  const onSubmit = async (data: CreateQlikConnectionInput) => {
    try {
      await createMutation.mutateAsync(data);
      onOpenChange(false);
    } catch {
      // surfaced via createMutation.error
    }
  };

  // Switching the method resets the form (a new baseline), so "edited" is simply
  // "anything typed, or a method other than the default".
  const typed = form.watch() as Record<string, unknown>;
  const dirty =
    authMethod !== "api_key" ||
    ["name", "baseUrl", "apiKey", "clientId", "clientSecret"].some((field) => !!typed[field]);

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={t("new.pageTitle")}
      description={t("new.description")}
      closeLabel={tc("close")}
      busy={createMutation.isPending}
      dirty={dirty}
      discardConfirmation={discardMessages()}
      restoreFocusRef={restoreFocusRef}
    >
      <div className="flex flex-col gap-4">
        <ConnectionFormFields
          mode="create"
          formId={CREATE_FORM_ID}
          authMethod={authMethod}
          onAuthMethodChange={handleMethodChange}
          register={form.register}
          errors={form.formState.errors}
          onSubmit={form.handleSubmit(onSubmit)}
          busy={createMutation.isPending}
          submitLabel={t("new.create")}
          submittingLabel={t("new.creating")}
          cancel={
            <DialogClose asChild>
              <Button variant="outline">{t("form.cancel")}</Button>
            </DialogClose>
          }
          submitError={createMutation.error ? getErrorMessage(createMutation.error) : undefined}
        />
        <ConnectionDrawerHelp authMethod={authMethod} />
      </div>
    </FormDrawer>
  );
}

// ---------------------------------------------------------------------------
// Edit
// ---------------------------------------------------------------------------

const EDIT_FORM_ID = "edit-connection-form";

export function EditConnectionDrawer({
  open,
  onOpenChange,
  connectionId,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string | undefined;
  /** The row's menu button, where the focus returns on close. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("connections");
  const { t: tc } = useTranslation("common");
  const {
    data: connection,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    ...connectionQueryOptions(connectionId ?? ""),
    enabled: open && !!connectionId,
  });
  const updateMutation = useUpdateConnection();
  const editConnectionFormSchema = useMemo(() => makeEditConnectionFormSchema(t), [t]);

  const currentMethod: QlikAuthMethod = connection?.authMethod ?? "api_key";
  const [authMethod, setAuthMethod] = useState<QlikAuthMethod>(currentMethod);
  useEffect(() => {
    setAuthMethod(currentMethod);
  }, [currentMethod]);

  const form = useForm<EditConnectionFormData>({
    resolver: zodResolver(editConnectionFormSchema),
    values: {
      name: connection?.name ?? "",
      baseUrl: connection?.baseUrl ?? "",
      apiKey: "",
      clientId: connection?.oauthClientId ?? "",
      clientSecret: "",
    },
  });

  const switching = authMethod !== currentMethod;

  async function runUpdate(payload: UpdateQlikConnectionInput) {
    if (!connectionId) return;
    try {
      await updateMutation.mutateAsync({ id: connectionId, data: payload });
      toast.success(t("edit.updated"));
      onOpenChange(false);
    } catch {
      // surfaced via updateMutation.error
    }
  }

  const onSubmit = async (data: EditConnectionFormData) => {
    if (switching) {
      if (authMethod === "api_key") {
        if (!data.apiKey) {
          toast.error(t("edit.apiKeyRequired"));
          return;
        }
        await runUpdate({
          authMethod: "api_key",
          name: data.name,
          baseUrl: data.baseUrl,
          apiKey: data.apiKey,
        });
        return;
      }
      if (!data.clientId || !data.clientSecret) {
        toast.error(t("edit.oauthRequired"));
        return;
      }
      await runUpdate({
        authMethod: "oauth_client_credentials",
        name: data.name,
        baseUrl: data.baseUrl,
        clientId: data.clientId,
        clientSecret: data.clientSecret,
      });
      return;
    }

    const updateData: UpdateQlikConnectionInput =
      authMethod === "oauth_client_credentials"
        ? { authMethod: "oauth_client_credentials" }
        : { authMethod: "api_key" };

    if (data.name !== connection?.name) updateData.name = data.name;
    if (data.baseUrl !== connection?.baseUrl) updateData.baseUrl = data.baseUrl;

    if (authMethod === "api_key") {
      if (data.apiKey) (updateData as { apiKey?: string }).apiKey = data.apiKey;
    } else {
      const base = updateData as { clientId?: string; clientSecret?: string };
      if (data.clientId && data.clientId !== connection?.oauthClientId)
        base.clientId = data.clientId;
      if (data.clientSecret) base.clientSecret = data.clientSecret;
    }

    if (Object.keys(updateData).length === 1) {
      toast.info(t("edit.noChanges"));
      return;
    }

    await runUpdate(updateData);
  };

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={t("edit.pageTitle")}
      description={connection ? t("edit.description", { name: connection.name }) : undefined}
      closeLabel={tc("close")}
      busy={updateMutation.isPending}
      dirty={form.formState.isDirty || switching}
      discardConfirmation={discardMessages()}
      restoreFocusRef={restoreFocusRef}
    >
      {isError ? (
        <FeedbackState
          state="error"
          title={t("edit.loadFailed")}
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              {tc("tryAgain")}
            </Button>
          }
        />
      ) : isLoading || !connection ? (
        <FeedbackState state="loading" title={t("edit.loading")} />
      ) : (
        <div className="flex flex-col gap-4">
          <ConnectionFormFields
            mode="edit"
            formId={EDIT_FORM_ID}
            authMethod={authMethod}
            onAuthMethodChange={setAuthMethod}
            register={form.register}
            errors={form.formState.errors}
            switching={switching}
            onSubmit={form.handleSubmit(onSubmit)}
            busy={updateMutation.isPending}
            submitLabel={t("edit.save")}
            submittingLabel={t("edit.saving")}
            cancel={
              <DialogClose asChild>
                <Button variant="outline">{t("form.cancel")}</Button>
              </DialogClose>
            }
            submitError={updateMutation.error ? getErrorMessage(updateMutation.error) : undefined}
          />
          <ConnectionDrawerHelp authMethod={authMethod} />
        </div>
      )}
    </FormDrawer>
  );
}
